import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";

import { getCategorySectionSelector } from "../../utils/categorySection";

import { StyledOverlayContent } from "./styles";

type ScrollableContentProps = PropsWithChildren<{
    scrollToTop: boolean;
    selectedCategory: string;
    // One-off target for the first paint only — lets a caller open the list already scrolled
    // to a section (e.g. past the permanent filters). Later category changes scroll as usual.
    initialScrollCategoryId?: string;
}>;

export const OverlayScrollableContent = ({
    selectedCategory,
    children,
    scrollToTop,
    initialScrollCategoryId,
}: ScrollableContentProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const isInitialMountRef = useRef(true);

    const observerRef = useRef<MutationObserver | null>(null);

    // Defer the scroll to after paint (rAF). Reading layout (scrollTo/scrollIntoView) inside a
    // layout effect runs synchronously mid-mount and forces a full recalc of the freshly-mounted
    // overlay tree; deferring lets the browser paint first and read settled layout.
    useEffect(() => {
        const container = contentRef.current;

        if (!container) {
            return;
        }

        const isInitialMount = isInitialMountRef.current;
        const behavior = isInitialMount ? "instant" : "smooth";
        const initialTargetCategory = isInitialMount ? initialScrollCategoryId : undefined;

        const frame = requestAnimationFrame(() => {
            isInitialMountRef.current = false;

            if (scrollToTop && !initialTargetCategory) {
                container.scrollTo?.({ top: 0, behavior });
                return;
            }

            const selector = getCategorySectionSelector(initialTargetCategory ?? selectedCategory);

            const scrollToSection = (): boolean => {
                const section = container.querySelector(selector);

                if (!section) {
                    return false;
                }

                section.scrollIntoView({ behavior, block: "start" });
                return true;
            };

            if (scrollToSection()) {
                return;
            }

            // The target section can mount a frame or two after the overlay opens (the filter content
            // is deferred to keep the open responsive), so watch for it and scroll once it appears.
            observerRef.current = new MutationObserver(() => {
                if (scrollToSection()) {
                    observerRef.current?.disconnect();
                    observerRef.current = null;
                }
            });

            observerRef.current.observe(container, { childList: true, subtree: true });
        });

        return () => {
            cancelAnimationFrame(frame);
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, [selectedCategory, scrollToTop, initialScrollCategoryId]);

    return <StyledOverlayContent ref={contentRef}>{children}</StyledOverlayContent>;
};
